import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { availabilityService } from '../services/availabilityService';
import { locationService } from '../services/locationService';
import { locationAvailabilityService } from '../services/locationAvailabilityService';
import { userService } from '../services/userService';

const AvailabilityEditForm = ({ studentId, studentName, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [locations, setLocations] = useState([]);
  const [locationAvailabilities, setLocationAvailabilities] = useState({});
  const [userAvailabilities, setUserAvailabilities] = useState({});
  const [studentInfo, setStudentInfo] = useState(null);
  
  // Load locations and their availabilities
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Get student information to find their school
        const studentInfoData = await userService.getUserById(studentId);
        if (!studentInfoData) {
          setError('Failed to load student information');
          return;
        }
        setStudentInfo(studentInfoData);
        
        // Load all locations
        const allLocations = await locationService.getAllActiveLocations();
        
        // Filter locations to show only:
        // 1. The student's school location
        // 2. GATE (hub location) - always available
        const filteredLocations = allLocations.filter(location => {
          // Always include GATE (hub location)
          if (location.isHub) return true;
          
          // Include the student's school
          if (studentInfoData.school && location.name === studentInfoData.school) return true;
          
          return false;
        });
        
        // Sort locations: hub location first, then student's school
        const sortedLocations = filteredLocations.sort((a, b) => {
          // Hub locations always come first
          if (a.isHub && !b.isHub) return -1;
          if (!a.isHub && b.isHub) return 1;
          
          // If both are hub or both are not hub, maintain original order
          return 0;
        });
        
        setLocations(sortedLocations);
        
        // Load location availabilities for filtered locations only
        const availabilities = {};
        for (const location of filteredLocations) {
          const locationAvails = await locationAvailabilityService.getAvailabilitiesByLocationId(location.id);
          availabilities[location.id] = locationAvails;
        }
        setLocationAvailabilities(availabilities);
        
        // Load existing user availabilities
        if (studentId) {
          const userAvails = await availabilityService.getUserAvailabilityByUserId(studentId);
          console.log('Loaded user availabilities:', userAvails);
          const userAvailObj = {};
          userAvails.forEach(avail => {
            const key = `${avail.locationId}_${avail.dayOfWeek}_${avail.startTime}_${avail.endTime}`;
            console.log('Creating key:', key, 'from availability:', avail);
            userAvailObj[key] = avail.isAvailable;
          });
          console.log('Final user availabilities object:', userAvailObj);
          setUserAvailabilities(userAvailObj);
        }
      } catch (err) {
        console.error('Error loading availability data:', err);
        setError('Failed to load availability data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [studentId]);
  
  const handleAvailabilityChange = (locationId, dayOfWeek, startTime, endTime, isAvailable) => {
    const key = `${locationId}_${dayOfWeek}_${startTime}_${endTime}`;
    setUserAvailabilities(prev => ({
      ...prev,
      [key]: isAvailable
    }));
  };
  
  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Convert user availabilities to the format expected by the API
      const slots = [];
      Object.entries(userAvailabilities).forEach(([key, isAvailable]) => {
        const [locationId, dayOfWeek, startTime, endTime] = key.split('_');
        
        // Validate and format the data
        if (!locationId || !dayOfWeek || !startTime || !endTime) {
          console.warn('Invalid slot data - missing required fields:', { locationId, dayOfWeek, startTime, endTime });
          return;
        }
        
        // Ensure proper data types and format
        const slot = {
          locationId: parseInt(locationId),
          dayOfWeek: dayOfWeek.toUpperCase(), // Ensure it's uppercase for enum parsing
          startTime: startTime, // Keep as HH:mm format for LocalTime.parse
          endTime: endTime, // Keep as HH:mm format for LocalTime.parse
          isAvailable: isAvailable
        };
        
        // Additional validation
        if (isNaN(slot.locationId) || slot.locationId <= 0) {
          console.warn('Invalid locationId:', slot.locationId);
          return;
        }
        
        // Validate day of week format
        const validDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
        if (!validDays.includes(slot.dayOfWeek)) {
          console.warn('Invalid dayOfWeek:', slot.dayOfWeek);
          return;
        }
        
        // Validate time format (HH:mm or HH:mm:ss)
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
        if (!timeRegex.test(slot.startTime) || !timeRegex.test(slot.endTime)) {
          console.warn('Invalid time format:', { startTime: slot.startTime, endTime: slot.endTime });
          return;
        }
        
        slots.push(slot);
      });
      
      if (slots.length === 0) {
        setError('No valid availability slots to save');
        setSaving(false);
        return;
      }
      
      console.log('Sending availability data:', { slots });
      
      await availabilityService.updateUserAvailability(studentId, { slots });
      setSuccess('Availability updated successfully');
      
      if (onSave) {
        onSave();
      }
    } catch (err) {
      console.error('Error saving availability:', err);
      setError('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };
  
  const formatTime = (time) => {
    // Parse the time string (expected format: "HH:mm" or "HH:mm:ss")
    const [hours, minutes] = time.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      return time; // Return original if parsing fails
    }
    
    // Convert to 12-hour format
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    
    // Format minutes with leading zero if needed
    const displayMinutes = minutes.toString().padStart(2, '0');
    
    return `${displayHours}:${displayMinutes} ${period}`;
  };
  
  const formatDay = (day) => {
    return day.charAt(0) + day.slice(1).toLowerCase();
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      <Typography variant="h6" sx={{ mb: 2 }}>
        Select Availability for {studentName || 'Student'}
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Please select the time slots when {studentName || 'the student'} is available. You can select availabilities for their school location and for GATE (our hub location).
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Please select all time slots for <strong>{studentName || 'the student'}</strong> that they would be available for.<br/>
          More availability options help teams form faster and increases the chances of finding compatible meeting times for all team members.
        </Typography>
      </Alert>
      
             {/* Group availabilities by location, then by day type (like original styling) */}
       {locations.map((location) => {
         const locationAvails = locationAvailabilities[location.id] || [];
         
         // Only show locations that have availabilities
         if (locationAvails.length === 0) return null;

         // Separate weekday and Saturday availabilities
         const weekdayAvails = locationAvails.filter(avail => 
           ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'].includes(avail.dayOfWeek)
         );
         const saturdayAvails = locationAvails.filter(avail => avail.dayOfWeek === 'SATURDAY');

         return (
           <Card key={location.id} sx={{ mb: 3 }}>
             <CardContent>
               <Typography variant="h6" sx={{ mb: 2, color: location.isHub ? 'primary.main' : 'text.primary' }}>
                 {location.name}
                 {location.isHub && ' (Hub Location)'}
                 {!location.isHub && studentInfo?.school === location.name && ' (Your School)'}
               </Typography>
               
               {/* Weekday Slots Section */}
               {weekdayAvails.length > 0 && (
                 <Box sx={{ mb: 3 }}>
                   <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
                     Weekday Slots
                   </Typography>
                   
                                       {/* Group by individual days in boxes */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'].map((day) => {
                        const dayAvails = weekdayAvails.filter(avail => avail.dayOfWeek === day);
                        if (dayAvails.length === 0) return null;

                        return (
                          <Card key={day} variant="outlined" sx={{ minWidth: 200, flex: '1 1 200px' }}>
                            <CardContent sx={{ py: 2, px: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary', textAlign: 'center' }}>
                                {formatDay(day)}
                              </Typography>
                              
                              {dayAvails.map((availability) => {
                                const key = `${location.id}_${availability.dayOfWeek}_${availability.startTime}_${availability.endTime}`;
                                const isChecked = userAvailabilities[key] || false;
                                
                                return (
                                  <Box key={availability.id} sx={{ mb: 1 }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={isChecked}
                                          onChange={(e) => handleAvailabilityChange(
                                            location.id,
                                            availability.dayOfWeek,
                                            availability.startTime,
                                            availability.endTime,
                                            e.target.checked
                                          )}
                                          size="small"
                                        />
                                      }
                                      label={
                                        <Typography variant="body2">
                                          {formatTime(availability.startTime)} - {formatTime(availability.endTime)}
                                        </Typography>
                                      }
                                    />
                                  </Box>
                                );
                              })}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                 </Box>
               )}
               
               {/* Saturday Slots Section */}
               {saturdayAvails.length > 0 && (
                 <Box sx={{ mb: 3 }}>
                   <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
                     Saturday Slots
                   </Typography>
                   
                   {/* Horizontal layout for Saturday slots like original */}
                   <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pl: 2 }}>
                     {saturdayAvails.map((availability) => {
                       const key = `${location.id}_${availability.dayOfWeek}_${availability.startTime}_${availability.endTime}`;
                       const isChecked = userAvailabilities[key] || false;
                       
                       return (
                         <FormControlLabel
                           key={availability.id}
                           control={
                             <Checkbox
                               checked={isChecked}
                               onChange={(e) => handleAvailabilityChange(
                                 location.id,
                                 availability.dayOfWeek,
                                 availability.startTime,
                                 availability.endTime,
                                 e.target.checked
                               )}
                               size="small"
                             />
                           }
                           label={
                             <Typography variant="body2">
                               {formatTime(availability.startTime)} - {formatTime(availability.endTime)}
                             </Typography>
                           }
                         />
                       );
                     })}
                   </Box>
                 </Box>
               )}
             </CardContent>
           </Card>
         );
       })}
      
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          size="large"
        >
          {saving ? <CircularProgress size={24} /> : 'Save Availability'}
        </Button>
      </Box>
    </Box>
  );
};

export default AvailabilityEditForm; 